import 'dayjs/locale/ca'
import 'dayjs/locale/da'
import 'dayjs/locale/de'
import 'dayjs/locale/en-gb'
import 'dayjs/locale/es'
import 'dayjs/locale/fi'
import 'dayjs/locale/fr'
import 'dayjs/locale/nb'
import 'dayjs/locale/nl'
import 'dayjs/locale/pl'
import 'dayjs/locale/pt'
import 'dayjs/locale/sv'

import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import bigIntSupport from 'dayjs/plugin/bigIntSupport'
import dayjsLocaleData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import Debug from 'debug'
import { action, makeAutoObservable } from 'mobx'
import { I18n, i18n } from '@lingui/core'
import { useLingui } from '@lingui/react'
import { useSuspenseQuery } from '@tanstack/react-query'

import type { Language } from '@jotta/grpc-connect-openapi/language'

import { setLanguageCookie } from './languageCookie'
import type { Locale } from './locale'
import { getDetectedLocale, localeInfoByCode, localeToLanguage } from './locale'

const debug = Debug('jotta:i18n:useLocale')

dayjs.extend(utc)
dayjs.extend(dayjsLocaleData)
dayjs.extend(localizedFormat)
dayjs.extend(bigIntSupport)
dayjs.extend(relativeTime)

export type PersistLanguage = (language: Language) => Promise<void>

export type DynamicActivateOptions = {
  /** Persist language for the authenticated customer. Omit to only switch local locale. */
  persistLanguage?: PersistLanguage
}

export function useActivateLocale(options: {
  /** Override locale detection and only use this locale */
  overrideLocale?: Locale
  /** Persist language for the authenticated customer. Omit in storybook / public surfaces. */
  persistLanguage?: PersistLanguage
}) {
  const locale = options?.overrideLocale || getDetectedLocale()
  const persistLanguage = options.persistLanguage

  const { isSuccess } = useSuspenseQuery({
    queryKey: ['i18n', locale, Boolean(persistLanguage)] as const,
    queryFn: ({ queryKey: [, nextLocale] }) => {
      debug('Detected locale %s', nextLocale)
      return dynamicActivate(nextLocale, { persistLanguage })
    },
    refetchOnWindowFocus: false,
  })

  return {
    isSuccess,
    i18n,
  }
}

export const observableLocale = makeAutoObservable(
  {
    // This is the observable version of the locale. Mobx stores will react to changes to this
    locale: getDetectedLocale(),
    // This is an untracked version of the locale that is not observable, use for reading
    // locale outside of a mobx reactive context
    localeUntracked: getDetectedLocale(),
    get languageCode() {
      return localeInfoByCode[this.locale as Locale].languageCode
    },
    setLocale(locale: Locale) {
      this.locale = locale
      this.localeUntracked = locale
      i18n.activate(locale)
      setLanguageCookie(locale)
      dayjs.locale(this.languageCode)
      debug('setLocale %s %s', locale, this.languageCode)
    },
    get dateFormatterMedium() {
      return new Intl.DateTimeFormat(this.languageCode, {
        dateStyle: 'medium',
      })
    },
    get dateFormatterShort() {
      return new Intl.DateTimeFormat(this.languageCode, {
        dateStyle: 'short',
      })
    },
    get dateTimeFormatterShort() {
      return new Intl.DateTimeFormat(this.languageCode, {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    },
    get dateTimeFormatterMedium() {
      return new Intl.DateTimeFormat(this.languageCode, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    },
  },
  {
    setLocale: action,
    // Disable observability for this property
    localeUntracked: false,
  },
)

async function switchLocale(locale: Locale, i18nObj: I18n = i18n) {
  switch (locale) {
    case 'da':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-da" */ './locales/da.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'de':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-de" */ './locales/de.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'en':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-en" */ './locales/en.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'fi':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-fi" */ './locales/fi.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'fr':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-fr" */ './locales/fr.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'nb':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-nb" */ './locales/nb.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'nl':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-nl" */ './locales/nl.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'sv':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-sv" */ './locales/sv.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'es':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-es" */ './locales/es.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'ca':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-ca" */ './locales/ca.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'pt':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-pt" */ './locales/pt.po'
        )
        i18nObj.load(locale, messages)
      }
      break
    case 'pl':
      {
        const { messages } = await import(
          /* webpackChunkName: "locale-pl" */ './locales/pl.po'
        )
        i18nObj.load(locale, messages)
      }
      break
  }
}

/**
 * We do a dynamic import of just the catalog that we need
 * @param locale any locale string
 * @param options Optional customer language persistence (kept out of @jotta/i18n deps)
 */
export async function dynamicActivate(
  locale: Locale,
  options: DynamicActivateOptions = {},
) {
  const update = observableLocale.localeUntracked !== locale
  const language = localeToLanguage[locale]
  if (options.persistLanguage && update && language) {
    await options.persistLanguage(language)
  }
  await switchLocale(locale)
  observableLocale.setLocale(locale)
  debug('Activated locale', locale)
  return true
}

export function useSubLocale(locale: Locale) {
  const [_i18n] = useState(() => {
    return new I18n({
      locale: i18n.locale,
      locales: i18n.locales,
      messages: { [i18n.locale]: i18n.messages },
    })
  })

  // const makeSafe = useSafePromise()

  const [isLoading, setIsLoading] = useState(locale !== i18n.locale)

  useEffect(() => {
    if (_i18n.locale === locale) {
      return
    }

    setIsLoading(true)

    switchLocale(locale, _i18n).then(() => {
      _i18n.activate(locale)
      setIsLoading(false)
    })
  }, [locale, _i18n])

  return { i18n: _i18n, isLoading }
}

export function getCurrentLanguage(): Language {
  return localeToLanguage[observableLocale.localeUntracked]
}

export function useLocale(): Locale {
  const lingui = useLingui()
  return lingui.i18n.locale as Locale
}

export function useCurrentLanguage(): Language {
  const locale = useLocale()
  return localeToLanguage[locale]
}
